/**
 * WebRTC peer connection to the Windows app.
 *
 * The phone is the offerer: it owns the camera, so it knows when there is
 * something worth negotiating. Media flows directly to the PC — the Worker only
 * shuttled the SDP and candidates that got us here.
 */

import type { IceCandidatePayload } from '../../shared/signaling/protocol.ts';
import { DATA_CHANNEL } from '../../shared/signaling/protocol.ts';
import type { DataChannelMessage } from '../../shared/signaling/protocol.ts';

export type PeerState = 'idle' | 'negotiating' | 'connected' | 'failed' | 'closed';

/** What the selected candidate pair actually is, read from getStats(). */
export interface TransportInfo {
  /** `host`, `srflx`, `prflx` or `relay`, straight from the stats report. */
  localType: string | null;
  remoteType: string | null
  localAddress: string | null;
  protocol: string | null;
  /** Milliseconds, only when the browser reports it. */
  rttMs: number | null;
  /** True only when the local candidate is a host candidate on a private
      address AND nothing is being relayed. Never inferred from anything else. */
  directLan: boolean;
}

export interface PeerEvents {
  state(state: PeerState, detail?: string): void;
  /** Local description is ready to send. */
  offer(sessionId: string, sdp: string): void;
  ice(sessionId: string, candidate: IceCandidatePayload | null): void;
  transport(info: TransportInfo): void;
  data(msg: DataChannelMessage): void;
}

const STATS_INTERVAL_MS = 2_000;

/**
 * The candidate stats fields we read. The DOM lib does not ship a
 * `RTCIceCandidateStats` type, and only these four are needed to describe the
 * selected pair honestly.
 */
interface CandidateStats {
  candidateType?: string;
  address?: string;
  protocol?: string;
  port?: number;
}

export class Peer {
  private pc: RTCPeerConnection | null = null;
  private channel: RTCDataChannel | null = null;
  private statsTimer: number | null = null;
  private sessionId = '';
  /** Candidates that arrived before the remote description was applied. */
  private earlyIce: IceCandidatePayload[] = [];
  private micSender: RTCRtpSender | null = null;
  private videoSender: RTCRtpSender | null = null;

  constructor(private readonly on: PeerEvents) {}

  get session(): string {
    return this.sessionId;
  }

  get connected(): boolean {
    return this.pc?.connectionState === 'connected';
  }

  /**
   * Builds a connection and offers the given tracks.
   *
   * STUN is configured by default so the pair connects whether the two ends share a
   * USB tether or are on different Wi-Fi networks. No media ever leaves the LAN
   * via a TURN relay unless both sides sit behind symmetric NATs — in that case
   * the connection fails visibly and the UI explains how to fix it.
   */
  async start(stream: MediaStream, iceServers: RTCIceServer[] = [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
  ]): Promise<void> {
    this.close();
    this.sessionId = crypto.randomUUID();

    const pc = new RTCPeerConnection({
      iceServers,
      /* `all` keeps host candidates in play, which is the whole point here. */
      iceTransportPolicy: 'all',
      bundlePolicy: 'max-bundle',
    });
    this.pc = pc;

    /* Data channel for push-to-talk control and transcript/reply text. It rides
       the same connection, so it costs no extra negotiation. */
    const channel = pc.createDataChannel(DATA_CHANNEL, { ordered: true });
    this.bindChannel(channel);

    for (const track of stream.getTracks()) {
      const sender = pc.addTrack(track, stream);
      if (track.kind === 'video') this.videoSender = sender;
    }

    pc.onicecandidate = (event) => {
      this.on.ice(this.sessionId, event.candidate ? toPayload(event.candidate) : null);
    };

    pc.onconnectionstatechange = () => {
      switch (pc.connectionState) {
        case 'connected':
          this.on.state('connected');
          this.startStats();
          break;
        case 'failed':
          this.on.state('failed', 'La connexion directe a échoué.');
          this.stopStats();
          break;
        case 'disconnected':
          this.on.state('negotiating', 'Lien interrompu, tentative de reprise.');
          break;
        case 'closed':
          this.on.state('closed');
          this.stopStats();
          break;
        default:
          break;
      }
    };

    pc.ondatachannel = (event) => this.bindChannel(event.channel);

    this.on.state('negotiating');
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    this.on.offer(this.sessionId, offer.sdp ?? '');
  }

  async acceptAnswer(sessionId: string, sdp: string): Promise<void> {
    if (!this.pc || sessionId !== this.sessionId) return;
    await this.pc.setRemoteDescription({ type: 'answer', sdp });
    /* Candidates that raced ahead of the answer are safe to add now. */
    for (const c of this.earlyIce.splice(0)) await this.addIce(sessionId, c);
  }

  async addIce(sessionId: string, candidate: IceCandidatePayload | null): Promise<void> {
    if (!this.pc || sessionId !== this.sessionId) return;
    if (!candidate) return;
    if (!this.pc.remoteDescription) {
      this.earlyIce.push(candidate);
      return;
    }
    try {
      await this.pc.addIceCandidate(candidate);
    } catch {
      /* A rejected candidate is normal during ICE; the pair that works wins. */
    }
  }

  /**
   * Swaps the outgoing camera track in place. The connection, the SSRC and the
   * PC's decoder all survive, so flipping the camera or changing resolution does
   * not interrupt what the PC is receiving.
   */
  async replaceVideoTrack(track: MediaStreamTrack): Promise<boolean> {
    if (!this.pc || !this.videoSender) return false;
    await this.videoSender.replaceTrack(track);
    return true;
  }

  /** Adds or removes a microphone track without renegotiating from scratch. */
  async setMicTrack(track: MediaStreamTrack | null): Promise<'renegotiate' | 'replaced' | 'none'> {
    if (!this.pc) return 'none';
    if (track) {
      if (this.micSender) {
        await this.micSender.replaceTrack(track);
        return 'replaced';
      }
      this.micSender = this.pc.addTrack(track);
      return 'renegotiate';
    }
    if (this.micSender) {
      await this.micSender.replaceTrack(null);
      return 'replaced';
    }
    return 'none';
  }

  /** Re-offers after a track was added. The PC answers on the same session. */
  async renegotiate(): Promise<{ sessionId: string; sdp: string } | null> {
    if (!this.pc) return null;
    const offer = await this.pc.createOffer();
    await this.pc.setLocalDescription(offer);
    return { sessionId: this.sessionId, sdp: offer.sdp ?? '' };
  }

  sendData(msg: DataChannelMessage): boolean {
    if (this.channel?.readyState !== 'open') return false;
    this.channel.send(JSON.stringify(msg));
    return true;
  }

  close(): void {
    this.stopStats();
    this.channel?.close();
    this.channel = null;
    this.micSender = null;
    this.videoSender = null;
    this.earlyIce = [];
    if (this.pc) {
      this.pc.onicecandidate = null;
      this.pc.onconnectionstatechange = null;
      this.pc.ondatachannel = null;
      this.pc.close();
      this.pc = null;
    }
  }

  /* --------------------------------------------------------------- stats -- */

  private startStats(): void {
    this.stopStats();
    void this.readStats();
    this.statsTimer = window.setInterval(() => void this.readStats(), STATS_INTERVAL_MS);
  }

  private stopStats(): void {
    if (this.statsTimer !== null) {
      clearInterval(this.statsTimer);
      this.statsTimer = null;
    }
  }

  /**
   * Reads the actually selected candidate pair. This is the only honest source
   * for "is this a direct link" — a constraint we requested proves nothing.
   */
  private async readStats(): Promise<void> {
    if (!this.pc) return;
    const report = await this.pc.getStats();

    let pair: RTCIceCandidatePairStats | null = null;
    for (const s of report.values()) {
      if (s.type === 'candidate-pair') {
        const p = s as RTCIceCandidatePairStats & { selected?: boolean };
        if (p.state === 'succeeded' && (p.nominated || p.selected)) pair = p;
      }
    }
    if (!pair) return;

    const local = pair.localCandidateId
      ? (report.get(pair.localCandidateId) as CandidateStats | undefined)
      : undefined;
    const remote = pair.remoteCandidateId
      ? (report.get(pair.remoteCandidateId) as CandidateStats | undefined)
      : undefined;

    const localType = local?.candidateType ?? null;
    const remoteType = remote?.candidateType ?? null;
    const address = local?.address ?? null;

    this.on.transport({
      localType,
      remoteType,
      localAddress: address,
      protocol: local?.protocol ?? null,
      rttMs:
        typeof pair.currentRoundTripTime === 'number'
          ? Math.round(pair.currentRoundTripTime * 1000)
          : null,
      /* Direct means: both ends are host candidates and nothing is relayed.
         A private address alone is not proof, and Wi-Fi must never be labelled
         as USB — so this flag says "direct LAN", not "USB". */
      directLan:
        localType === 'host' &&
        remoteType === 'host' &&
        isPrivate(address) &&
        isPrivate(remote?.address ?? null),
    });
  }

  private bindChannel(channel: RTCDataChannel): void {
    if (channel.label !== DATA_CHANNEL) return;
    this.channel = channel;
    channel.onmessage = (event) => {
      try {
        this.on.data(JSON.parse(event.data as string) as DataChannelMessage);
      } catch {
        /* A malformed control frame is dropped rather than guessed at. */
      }
    };
  }
}

/* ----------------------------------------------------------------- utils -- */

function toPayload(c: RTCIceCandidate): IceCandidatePayload {
  return {
    candidate: c.candidate,
    sdpMid: c.sdpMid,
    sdpMLineIndex: c.sdpMLineIndex,
    usernameFragment: c.usernameFragment,
  };
}

/** RFC1918 plus the Android USB-tether range, and IPv6 link-local. */
function isPrivate(address: string | null): boolean {
  if (!address) return false;
  if (/^10\./.test(address)) return true;
  if (/^192\.168\./.test(address)) return true;
  if (/^172\.(1[6-9]|2\d|3[01])\./.test(address)) return true;
  if (/^169\.254\./.test(address)) return true;
  if (/^fe80:/i.test(address)) return true;
  return false;
}
